import {
  Collapse,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Paper,
  Theme,
  withStyles,
  WithStyles,
  withWidth
} from '@material-ui/core'
import { WithWidth } from '@material-ui/core/withWidth'
import classNames from 'classnames'
import i18next from 'i18next'
import ChevronDownIcon from 'mdi-react/ChevronDownIcon'
import ChevronUpIcon from 'mdi-react/ChevronUpIcon'
import { FC, Fragment } from 'react'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import { openOrCloseListNest } from '../../../actions/lists'
import { OpenOrCloseListNest } from '../../../actions/types'
import { isSmallScreen } from '../../../libs/screenSize'
import { selectNestedList } from '../../../selectors/lists'
import { StateInterface } from '../../../types/states'
import { afterHalfPoint } from '../../../utils/arrays'

const styles = ({ spacing, breakpoints }: Theme) => ({
  listItem: {
    padding: '0.15em',
    marginLeft: spacing(),
    backgroundColor: 'paper' as any
  },
  listItemText: {
    userSelect: 'text' as const
  },
  nestedList: {
    padding: 0,
    backgroundColor: 'paper' as any
  },
  otherOwnerList: {
    padding: 0,
    marginBottom: 0,
    marginLeft: spacing(2)
  },
  reservationsList: {
    padding: 0,
    marginBottom: spacing(2),
    marginLeft: spacing(2),
    fontWeight: 'bold' as any
  },
  applicationsList: {
    padding: 0,
    marginBottom: spacing(2),
    marginLeft: spacing(2)
  },
  openLeft: {
    marginLeft: '-110%'
  },
  innerList: {
    textAlign: 'left' as any,
    display: 'grid',
    gridAutoFlow: 'column',
    backgroundColor: 'inherit',
    [breakpoints.down('sm')]: {
      gridAutoFlow: 'unset'
    }
  },
  doubleWide: {
    width: '200%',
    maxWidth: '95vw',
    textAlign: 'left' as any
  },
  firstHalf: {
    gridColumnStart: 1,
    textAlign: 'left' as any
  },
  secondHalf: {
    gridColumnStart: 2,
    textAlign: 'left' as any
  }
})

export interface NestedListProps extends WithStyles, WithWidth {
  openOrCloseListNest: OpenOrCloseListNest
  nestedList: boolean
  id: string
  isOpen: boolean
  listItemText: any
  data: any
  informationOrder?: string[]
  translationPath: string
  Form: any
  Buttons?: any
  listName?: string
  NestedListChild?: NestedListProps
  doubleWide?: boolean
  openDirection?: 'left' | 'right'
  t: i18next.TFunction
}

const isNestOpen = (nestedList: any, nestId: string) =>
  nestedList ? nestedList.id === nestId && nestedList.isOpen : false

/** Usage examples:
 * Check ReservationsList, ApplicationsList, or other ApartmentInformation list components.
 *
 * Usage: Use informationOrder and data for a simple nest to list from data.
 *
 * informationOrder maps through data and creates an inner list of the data
 *
 * You can nest a nested list into NestedListChild or if you don't want to double nest,
 *
 * listName is used as index in state, if no listName specified uses "nestedList" as index
 *
 * Buttons are for example for accept/reject buttons in applicationsList
 * Form is for example for adminNotes in applicationsList
 */
const NestedList: FC<NestedListProps> = ({
  classes,
  t,
  openOrCloseListNest: openOrCloseListNestAction,
  id,
  nestedList,
  listName,
  isOpen = isNestOpen(nestedList, id),
  listItemText,
  data,
  informationOrder,
  translationPath,
  Form,
  Buttons,
  NestedListChild,
  doubleWide,
  openDirection,
  width
}) => (
  <Fragment key={`${id}`}>
    <ListSubheader
      className={listName ? classes[`${listName}`] : classes[`nestedList`]}
    >
      <ListItem
        button
        onClick={() =>
          openOrCloseListNestAction({
            listName: listName ? listName : 'nestedList',
            id,
            isOpen: !isOpen
          })
        }
      >
        <ListItemText primary={listItemText} className={classes.listItemText} />
        {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
      </ListItem>
    </ListSubheader>
    <Collapse
      in={isOpen}
      timeout='auto'
      unmountOnExit
      className={classNames(openDirection === 'left' && classes.openLeft)}
    >
      {NestedListChild
        ? NestedListChild
        : informationOrder &&
          data && (
            <List component={'div' as any} disablePadding>
              <Grid
                container
                direction={isSmallScreen(width) ? 'column' : 'row'}
              >
                <Paper className={classes[`${listName}`]}>
                  <Grid
                    alignContent='flex-start'
                    item
                    xs={12}
                    container
                    direction='column'
                    className={classNames(
                      classes.innerList,
                      doubleWide && !isSmallScreen(width) && classes.doubleWide
                    )}
                  >
                    {informationOrder.map((key: string, index: number) => (
                      <Grid
                        key={key}
                        className={classNames(
                          !afterHalfPoint(informationOrder, index) &&
                            !isSmallScreen(width) &&
                            classes.firstHalf,
                          afterHalfPoint(informationOrder, index) &&
                            !isSmallScreen(width) &&
                            classes.secondHalf
                        )}
                      >
                        <ListItem
                          key={`${key}+${id}`}
                          className={classes.listItem}
                        >
                          <ListItemText
                            primary={t(`${translationPath}${key}`)}
                            secondary={data[key] ? t(`${data[key]}`) : '-'}
                          />
                        </ListItem>
                      </Grid>
                    ))}
                  </Grid>
                  <Grid item xs={12}>
                    {Buttons}
                    {Form}
                  </Grid>
                </Paper>
              </Grid>
            </List>
          )}
    </Collapse>
  </Fragment>
)

const mapStateToProps = (
  state: StateInterface,
  { listName }: NestedListProps
) => ({
  nestedList: selectNestedList(listName ? listName : 'nestedList')(state)
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  openOrCloseListNest: (nestedList: {
    listName: string
    id: string
    isOpen: boolean
  }) => dispatch(openOrCloseListNest(nestedList))
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(withWidth()(NestedList) as any) as any) as any
